import { observer } from '@formily/react';
import { useConfig, useStore } from '@yimoka/react';
import { ListStore } from '@yimoka/store';
import { Button, ButtonProps, Col, ColProps, Flex, Space, theme } from 'antd';
import React, { createContext, useContext } from 'react';

import { useLocaleComponent } from '../hooks/use-locale';

import { StoreForm, StoreFormProps } from './form';
import { Submit } from './submit';

const { useToken } = theme;

export const ListFilterContext = createContext(false);

/** 返回当前字段是否位于列表筛选器内，供输入组件选择适合筛选场景的交互。 */
export const useListFilterContext = () => useContext(ListFilterContext);

// ListFilter 渲染列表筛选条件并响应筛选 Store 的状态变化。
export const ListFilter = observer((props: ListFilterProps) => {
  const {
    labelAttached = true,
    isReset = true,
    resetFetch = false,
    store,
    queryProps,
    resetProps,
    actionCol,
    col,
    children,
    row = true,
    ...rest
  } = props;
  const curStore = useStore(store);
  const { useLocation, useNavigate } = useConfig();
  const location = useLocation();
  const navigate = useNavigate();
  const { onClick: resetOnClick, ...restResetProps } = resetProps ?? {};
  const token = useToken();
  const locale = useLocaleComponent('ListFilter');
  const filterRow = getFilterRow(row, token.token.margin);
  const filterCol = getResponsiveFilterCol(col);
  const filterActionCol = getResponsiveFilterCol(actionCol ?? col);

  /** resetValues 根据页面显式开关决定是否立即回读列表，并同步清理路由筛选参数。 */
  const resetValues = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!resetFetch) {
      curStore?.resetValues();
    } else {
      void curStore?.fetchWithReset();
      if (curStore instanceof ListStore && curStore.options.bindRoute) {
        const search = curStore.genURLSearch();
        navigate(search ? `${location.pathname}?${search}` : location.pathname, {
          replace: curStore.options.updateRouteType === 'replace',
        });
      }
    }
    resetOnClick?.(event);
  };

  return (
    <ListFilterContext.Provider value>
      <StoreForm
        col={filterCol}
        colon={false}
        labelAttached={labelAttached}
        row={filterRow}
        {...rest}
      >
        {children}
        <Col
          {...filterActionCol}
          style={{ marginLeft: 'auto', ...actionCol?.style }}>
          <Flex wrap justify='end' style={{ marginBottom: token.token.margin }} >
            <Space wrap>
              <Submit children={locale.query} store={curStore} {...queryProps} />
              {isReset && (
                <Button
                  children={locale.reset}
                  {...restResetProps}
                  onClick={resetValues}
                />
              )}
            </Space>
          </Flex>
        </Col>
      </StoreForm >
    </ListFilterContext.Provider>
  );
});

/** 合并列表筛选器的响应式列配置。 */
function getResponsiveFilterCol(col?: ColProps): ColProps {
  return {
    xs: 24,
    sm: 12,
    lg: 8,
    ...col,
  };
}

/** 将标量 gutter 仅用于横向列间距，避免与 Form.Item 自带的下边距重复。 */
function getFilterRow(row: StoreFormProps['row'], horizontalGap: number): StoreFormProps['row'] {
  if (!row) {
    return row;
  }
  if (row === true) {
    return { gutter: [horizontalGap, 0] };
  }

  const { gutter, ...rest } = row;
  if (Array.isArray(gutter)) {
    return { ...rest, gutter: [gutter[0], gutter[1] ?? 0] };
  }
  return { ...rest, gutter: [gutter ?? horizontalGap, 0] };
}

export interface ListFilterProps extends StoreFormProps {
  isReset?: boolean;
  resetFetch?: boolean;
  queryProps?: ButtonProps;
  resetProps?: ButtonProps;
  actionCol?: ColProps;
}
